import { type FC } from 'react';

import {
    Anchor,
    Body1,
    Flex,
    Stack,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { useDisclosure } from '@myphx-engineering/phx-design-system/hooks';

import type { DigitallyReceivedPrescriberOrderInfoFragment } from '../../../graphql/generated/graphql';
import type {
    NdcDefinition,
    NdcSwitchConfig,
} from '../../../types/drug-config';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { ConditionalComponent } from '../../utility/ConditionalComponent';
import { useDrugSwitchConfig } from '../switch-prescription/hooks/use-drug-switch-config';
import { SwitchOtherOptionsModal } from '../switch-prescription/SwitchOtherOptionsModal';

export type ViewSwitchOptionsProps = {
    ndcDefinition: NdcDefinition;
    order: DigitallyReceivedPrescriberOrderInfoFragment;
    position: NdcSwitchConfig['link']['position'];
};

const ButtonIcon: FC<{ icon: NdcSwitchConfig['link']['icon'] }> = ({
    icon,
}) => {
    if (!icon) {
        return null;
    }

    if (icon.url) {
        // oxlint-disable-next-line jsx-a11y/alt-text -- tracked in docs/oxlint-violations.md
        return <img src={icon.url} aria-hidden="true" height="21" width="21" />;
    }

    if (icon.name) {
        /*
         * @TODO: Eventually we should return something like this, but the
         * `Icon` component in the design system doesn't support it (yet)
         *
         * return (<PhxIcon name={icon.name} />);
         */
        return null;
    }

    return null;
};

export const ViewSwitchOptions: FC<ViewSwitchOptionsProps> = ({
    ndcDefinition,
    order,
    position,
}) => {
    const drugSwitchConfig = useDrugSwitchConfig(ndcDefinition.ndc);
    const [isOpen, { toggle }] = useDisclosure(false);

    if (!drugSwitchConfig) {
        return null;
    }

    const { link } = drugSwitchConfig;
    if (link.position !== position) {
        return null;
    }

    const { heading, text, icon } = link;

    const hasTargets = drugSwitchConfig.targets.length > 0;

    return (
        <Stack gap={0} justify={position === 'details' ? 'center' : 'start'}>
            <ConditionalComponent condition={!!heading}>
                <Body1 bold highContrast>
                    {heading}
                </Body1>
            </ConditionalComponent>

            <ConditionalComponent condition={hasTargets}>
                <Anchor
                    href={getAbsoluteRoute(
                        'order.prescriberOrderId.switch.root',
                        {
                            prescriberOrderId: order.drxId ?? '',
                        }
                    )}
                    textDecoration="none"
                >
                    <Flex
                        gap="xxxs"
                        justify={position === 'details' ? 'center' : 'start'}
                    >
                        <ButtonIcon icon={icon} />
                        <Body1 color="primary" bold>
                            {text}
                        </Body1>
                    </Flex>
                </Anchor>
            </ConditionalComponent>
            <ConditionalComponent condition={!hasTargets}>
                <TextButton
                    onClick={toggle}
                    justify={position === 'details' ? 'center' : 'start'}
                >
                    {text}
                </TextButton>
                <SwitchOtherOptionsModal
                    isOpen={isOpen}
                    order={order}
                    toggle={toggle}
                />
            </ConditionalComponent>
        </Stack>
    );
};
